"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import { capture } from "@/lib/analytics/events";
import { submitFeedback, type FeedbackActionResult } from "@/lib/feedback-actions";

/**
 * Shared "tell us" widget — replaces plain `mailto:` links (ReportIssueLink,
 * NoResultsFeedback, /coverage) that do nothing on a visitor's machine with
 * no default mail client, and leave no record of what was actually typed.
 * Click reveals an inline textarea; submit posts to `submitFeedback`
 * (best-effort email; on failure the full text is instead captured to
 * PostHog, see below, so it is never silently dropped) and shows an HONEST
 * confirmation — "sent to our team" only when delivery actually succeeded,
 * "we've got it" otherwise, never claiming more than what happened.
 */
export function FeedbackLink({
  kind,
  prompt,
  placeholder,
  context,
  quickReplies,
  className = "text-neutral-500 underline underline-offset-2 hover:text-brand-700",
  oneClickOptions,
}: {
  /** Event/email tag, e.g. "job_issue", "no_results", "coverage_missing_employer". */
  kind: string;
  prompt: string;
  placeholder: string;
  /** Extra context sent with the email (job URL, search query, filters…). */
  context?: string;
  /**
   * Optional one-click starting phrases (e.g. "Couldn't find the right
   * jobs"). Clicking fills the textarea and focuses it rather than
   * submitting immediately — a blank textarea is real writing effort right
   * when a visitor is trying to leave, a pre-filled one just needs editing
   * or a straight submit.
   */
  quickReplies?: string[];
  /**
   * 2026-09-29: the old default (text-neutral-400, underline only on hover)
   * was the exact low-contrast trap ReportIssueLink's own fix already
   * documented — every call site still relying on this default
   * (dashboard_feedback, video_applications_feedback) had 0 feedback_opened
   * events in 30 days, same as every call site that had copy-pasted the same
   * losing className explicitly (now fixed at each of those call sites too).
   */
  className?: string;
  /**
   * T-284 (2026-10-09): `quickReplies` still cost 3 actions (open → pick →
   * Send) and premium_pitch_dismissed/video_app_pitch_dismissed both sat at
   * 0 feedback_opened against hundreds of pitch views for 30+ days straight
   * — the textarea itself, not the copy, looked like the friction. When set,
   * `oneClickOptions` skips the "open a textarea" step entirely: the pills
   * render immediately and a single click submits that option as the whole
   * message via the same `action`, no typed confirmation required. A
   * "something else" fallback still reaches the full write-in form below.
   */
  oneClickOptions?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [result, action, pending] = useActionState<
    FeedbackActionResult | null,
    FormData
  >(submitFeedback, null);

  // Fires once the server action resolves, so `delivered` reflects what
  // actually happened rather than a guess made at submit time. On a failed
  // delivery (rate-limited or Resend error) the message has nowhere else to
  // live — feedback-actions.ts has no DB fallback — so the full text rides
  // along here specifically so it isn't lost (T-267).
  const captured = useRef(false);
  useEffect(() => {
    if (result?.ok && !captured.current) {
      captured.current = true;
      const delivered = result.delivered === true;
      capture("feedback_submitted", {
        kind,
        message_length: messageLength,
        delivered,
        ...(delivered ? {} : { message_on_failure: message.slice(0, 4000) }),
      });
    }
  }, [result, kind, messageLength, message]);

  if (result?.ok) {
    return (
      <p className={className}>
        {result.delivered ? "Thanks — sent to our team." : "Thanks — we've got it."}
      </p>
    );
  }

  if (oneClickOptions && oneClickOptions.length > 0 && !open) {
    return (
      <div className={className}>
        <p>{prompt}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {oneClickOptions.map((option) => (
            <button
              key={option}
              type="button"
              disabled={pending}
              onClick={() => {
                capture("feedback_opened", { kind });
                setMessage(option);
                setMessageLength(option.trim().length);
                const fd = new FormData();
                fd.set("kind", kind);
                if (context) fd.set("context", context);
                fd.set("message", option);
                action(fd);
              }}
              className="rounded-full border border-neutral-300 px-2.5 py-1 text-xs text-neutral-600 hover:border-brand-600 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {option}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            capture("feedback_opened", { kind });
            setOpen(true);
          }}
          className="mt-1.5 text-[11px] text-neutral-400 underline underline-offset-2 hover:text-brand-700"
        >
          Something else?
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          // 0 feedback_submitted events site-wide in 30 days gave no way to
          // tell "nobody clicks this link" from "people open it and bail" —
          // this is the missing middle step of that funnel.
          capture("feedback_opened", { kind });
          setOpen(true);
        }}
        className={className}
      >
        {prompt}
      </button>
    );
  }

  return (
    <form action={action} className="mt-2 flex max-w-md flex-col gap-2">
      <input type="hidden" name="kind" value={kind} />
      {context ? <input type="hidden" name="context" value={context} /> : null}
      {quickReplies && quickReplies.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {quickReplies.map((reply) => (
            <button
              key={reply}
              type="button"
              onClick={() => {
                setMessage(reply);
                setMessageLength(reply.trim().length);
                textareaRef.current?.focus();
              }}
              className="rounded-full border border-neutral-300 px-2.5 py-1 text-xs text-neutral-600 hover:border-brand-600 hover:text-brand-700"
            >
              {reply}
            </button>
          ))}
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        name="message"
        required
        rows={3}
        placeholder={placeholder}
        value={message}
        onChange={(e) => {
          setMessage(e.currentTarget.value);
          setMessageLength(e.currentTarget.value.trim().length);
        }}
        className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
      />
      <input
        type="email"
        name="email"
        placeholder="Your email (optional — only if you want a reply)"
        className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
      />
      {result && !result.ok ? (
        <p className="text-xs text-stamp-700">{result.message}</p>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? "Sending…" : "Send"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[13px] font-medium text-neutral-500 hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
